import {Component, Input, OnInit} from '@angular/core';
import {SaveMessage} from '../../../_helpers/save-message';
import {LicenzeService} from '../../../_services/licenze.service';
import {RoleService} from '../../../_services/role.service';
import {OrganizzazioniService} from '../../../_services/organizzazioni.service';
import {MenuAccessService} from '../../../_services/menu-access.service';
import {Licenza, MenuConfig, MenuVoceBinding, Modulo, parseMenuConfig, Role, serializeMenuConfig} from '../../../_models/_Index';
import {MENU_SEZIONI, MenuVoce, vociConfigurabiliDiSezione} from '../../../_helpers/menu-voci';

/** Contesto "licenza": vale per tutti i ruoli dell'organizzazione. */
const CONTESTO_LICENZA = '';

interface RigaMenuOrg {
  voce: MenuVoce;
  /** Codice del modulo che sblocca la voce, secondo il binding globale effettivo. */
  moduloRichiesto: string;
  /** Ruoli che possono vedere la voce secondo il binding globale effettivo. Vuoto = tutti. */
  ruoliRichiesti: string[];
  /** Il binding globale non ha spento la voce. */
  bindingAttivo: boolean;
  /** L'organizzazione possiede il modulo richiesto. */
  coperta: boolean;
  /** Spunta: la voce è utilizzabile nel contesto selezionato. */
  abilitata: boolean;
}

interface GruppoMenuOrg {
  sezione: MenuVoce;
  righe: RigaMenuOrg[];
}

/**
 * Rifinitura del menù per una singola organizzazione. Solo SOTTRATTIVA: togliere una spunta nasconde
 * una voce già coperta da licenza e ruolo, spuntarla non può renderne visibile una non licenziata.
 *
 * A interruttore spento la configurazione è inerte e il menù si comporta come prima.
 */
@Component({
  selector: 'app-licenze-menu-config',
  templateUrl: './licenze-menu-config.component.html',
  styleUrls: ['./licenze-menu-config.component.scss'],
  standalone: false
})
export class LicenzeMenuConfigComponent implements OnInit {
  @Input() organizzazioneId: number;
  @Input() licenza: Licenza;

  gruppi: GruppoMenuOrg[] = [];
  ruoliDisponibili: Role[] = [];
  contesto = CONTESTO_LICENZA;
  config: MenuConfig;
  ricerca = '';
  sezioniAperte: string[] = [];
  loading = false;
  salvataggio = false;

  // Moduli posseduti dall'ORGANIZZAZIONE IN MODIFICA, che può non essere quella attiva:
  // checkPermesso() ragiona sull'organizzazione attiva e qui non sarebbe corretto.
  private moduliOrganizzazione: Modulo[] = [];
  private bindings: MenuVoceBinding[] = [];
  private menuConfigSalvato = '';
  private gruppiVisibiliCorrenti: GruppoMenuOrg[] = [];

  constructor(
    private saveMessage: SaveMessage,
    private licenzeService: LicenzeService,
    private roleService: RoleService,
    private organizzazioniService: OrganizzazioniService,
    private menuAccess: MenuAccessService) { }

  get contestoLicenza(): boolean {
    return this.contesto === CONTESTO_LICENZA;
  }

  get contestoDescrizione(): string {
    if (this.contestoLicenza) {
      return 'tutti i ruoli';
    }

    const ruolo = this.ruoliDisponibili.find(r => r.codice === this.contesto);
    return ruolo?.descrizione?.trim() || ruolo?.codice || this.contesto;
  }

  get disponibiliNelContesto(): number {
    return this.tutteLeRighe().filter(riga => this.disponibileNelContesto(riga)).length;
  }

  get selezionateNelContesto(): number {
    return this.tutteLeRighe()
      .filter(riga => this.disponibileNelContesto(riga) && riga.abilitata)
      .length;
  }

  get escluseNelContesto(): number {
    return this.tutteLeRighe()
      .filter(riga => this.disponibileNelContesto(riga) && !riga.abilitata)
      .length;
  }

  get haModificheDaSalvare(): boolean {
    return !!this.config
      && serializeMenuConfig(this.configConContestoCorrente()) !== this.menuConfigSalvato;
  }

  ngOnInit() {
    this.config = parseMenuConfig(this.licenza?.menuConfig);
    void this.carica();
  }

  async carica(): Promise<void> {
    this.loading = true;
    try {
      const [moduli, ruoli, bindings] = await Promise.all([
        this.licenzeService.getModuliOfOrgAsync(this.organizzazioneId),
        this.roleService.getRoles(),
        this.licenzeService.refreshMenuBindingsAsync()
      ]);

      this.moduliOrganizzazione = moduli ?? [];
      this.ruoliDisponibili = (ruoli ?? []).filter(r => r.isActive);
      this.bindings = bindings;
      this.componiRighe();
      this.menuConfigSalvato = serializeMenuConfig(this.configConContestoCorrente());
    } catch {
      this.saveMessage.messageError('Ci sono problemi con il caricamento della configurazione del menù.');
    } finally {
      this.loading = false;
    }
  }

  /** Il cambio contesto salva in memoria le spunte correnti prima di ricomporre l'elenco. */
  cambiaContesto(nuovoContesto: string): void {
    this.registraContestoCorrente();
    this.contesto = nuovoContesto ?? CONTESTO_LICENZA;
    this.componiRighe();
  }

  async salva(): Promise<void> {
    this.registraContestoCorrente();
    this.salvataggio = true;
    try {
      const menuConfig = serializeMenuConfig(this.config);
      this.licenza.menuConfig = menuConfig;
      await this.licenzeService.updateLicenzaAsync(this.licenza);
      const licenzaSalvata = await this.licenzeService.getLicenzaFromIdAsync(this.organizzazioneId);
      if (licenzaSalvata?.menuConfig !== menuConfig) {
        this.saveMessage.messageError('Ci sono problemi con il salvataggio della configurazione del menù.');
        return;
      }

      this.licenza = licenzaSalvata;
      this.menuConfigSalvato = menuConfig;
      this.saveMessage.messageInfo('Configurazione del menù salvata.');
      await this.refreshPermessiOrganizzazioneAttiva();
    } catch {
      this.saveMessage.messageError('Ci sono problemi con il salvataggio della configurazione del menù.');
    } finally {
      this.salvataggio = false;
    }
  }

  /** Rimette tutte le spunte del contesto corrente, cioè svuota le esclusioni. */
  abilitaTutte(): void {
    this.gruppi.forEach(g => g.righe.forEach(r => r.abilitata = true));
  }

  cerca(valore: string): void {
    this.ricerca = valore ?? '';
    this.aggiornaGruppiVisibili();
    if (this.ricerca.trim()) {
      this.sezioniAperte = this.gruppiVisibiliCorrenti.map(gruppo => gruppo.sezione.id);
    }
  }

  gruppiVisibili(): GruppoMenuOrg[] {
    return this.gruppiVisibiliCorrenti;
  }

  tracciaSezione(_indice: number, gruppo: GruppoMenuOrg): string {
    return gruppo.sezione.id;
  }

  disponibili(gruppo: GruppoMenuOrg): number {
    return gruppo.righe.filter(riga => this.disponibileNelContesto(riga)).length;
  }

  escluse(gruppo: GruppoMenuOrg): number {
    return gruppo.righe
      .filter(riga => this.disponibileNelContesto(riga) && !riga.abilitata)
      .length;
  }

  disponibileNelContesto(riga: RigaMenuOrg): boolean {
    if (!riga.coperta) {
      return false;
    }
    if (this.contestoLicenza || riga.ruoliRichiesti.length === 0) {
      return true;
    }
    return riga.ruoliRichiesti.includes(this.contesto);
  }

  /** Esclusione generale conservata anche quando il filtro è spento, senza alterare la scelta del ruolo. */
  esclusaPerTutti(riga: RigaMenuOrg): boolean {
    return !this.contestoLicenza && this.config.disabled.includes(riga.voce.id);
  }

  descrizioneStato(riga: RigaMenuOrg): string {
    if (!riga.bindingAttivo) {
      return 'Voce disattivata nella Mappatura menù globale.';
    }
    if (!riga.coperta && riga.moduloRichiesto) {
      return `Richiede il modulo ${riga.moduloRichiesto}, non attivo per questa organizzazione.`;
    }
    if (!this.disponibileNelContesto(riga)) {
      return `Il ruolo ${this.contestoDescrizione} non è abilitato dalla Mappatura menù globale.`;
    }

    const dettagli: string[] = [];
    if (riga.moduloRichiesto) {
      dettagli.push(`Modulo: ${riga.moduloRichiesto}`);
    }
    if (riga.ruoliRichiesti.length > 0) {
      dettagli.push(`Ruoli: ${riga.ruoliRichiesti.join(', ')}`);
    }
    return dettagli.length > 0
      ? dettagli.join(' · ')
      : 'Voce disponibile senza requisiti aggiuntivi.';
  }

  private aggiornaGruppiVisibili(): void {
    const termine = this.ricerca.trim().toLocaleLowerCase('it-IT');
    if (!termine) {
      this.gruppiVisibiliCorrenti = this.gruppi;
      return;
    }

    this.gruppiVisibiliCorrenti = this.gruppi
      .map(gruppo => ({
        sezione: gruppo.sezione,
        righe: gruppo.righe.filter(riga =>
          riga.voce.label.toLocaleLowerCase('it-IT').includes(termine) ||
          riga.moduloRichiesto.toLocaleLowerCase('it-IT').includes(termine))
      }))
      .filter(gruppo => gruppo.righe.length > 0);
  }

  private componiRighe(): void {
    const esclusi = this.esclusiDelContesto();

    this.gruppi = MENU_SEZIONI
      .map(sezione => ({
        sezione,
        righe: vociConfigurabiliDiSezione(sezione.id).map(voce => this.rigaDa(voce, esclusi))
      }))
      .filter(gruppo => gruppo.righe.length > 0);
    this.aggiornaGruppiVisibili();
  }

  private rigaDa(voce: MenuVoce, esclusi: string[]): RigaMenuOrg {
    const binding = this.menuAccess.effectiveBinding(voce, this.bindings);
    return {
      voce,
      moduloRichiesto: binding.modulo,
      ruoliRichiesti: binding.ruoli,
      bindingAttivo: binding.isActive,
      coperta: binding.isActive && this.menuAccess.isCopertaDa(voce, this.moduliOrganizzazione, this.bindings),
      abilitata: !esclusi.includes(voce.id)
    };
  }

  private tutteLeRighe(): RigaMenuOrg[] {
    return this.gruppi.flatMap(gruppo => gruppo.righe);
  }

  private esclusiDelContesto(): string[] {
    return this.contestoLicenza
      ? this.config.disabled
      : (this.config.ruoli[this.contesto] ?? []);
  }

  private registraContestoCorrente(): void {
    const config = this.configConContestoCorrente();
    this.config.disabled = config.disabled;
    this.config.ruoli = config.ruoli;
  }

  private configConContestoCorrente(): MenuConfig {
    const config: MenuConfig = {
      enabled: this.config.enabled,
      disabled: [...this.config.disabled],
      ruoli: Object.fromEntries(Object.entries(this.config.ruoli).map(([ruolo, voci]) => [ruolo, [...voci]]))
    };
    const esclusi = this.tutteLeRighe().filter(riga => !riga.abilitata).map(riga => riga.voce.id);

    if (this.contestoLicenza) {
      config.disabled = esclusi;
    } else if (esclusi.length > 0) {
      config.ruoli[this.contesto] = esclusi;
    } else {
      delete config.ruoli[this.contesto];
    }

    return config;
  }

  private async refreshPermessiOrganizzazioneAttiva(): Promise<void> {
    const organizzazioneId = +this.organizzazioneId;
    if (!organizzazioneId || organizzazioneId !== this.organizzazioniService.organizzazioneId) {
      return;
    }

    try {
      await this.licenzeService.refreshModuliOfOrgAsync(organizzazioneId);
    } catch {
      this.saveMessage.messageWarning(
        'Configurazione salvata, ma il menù corrente non è stato aggiornato. Ricaricare la pagina.'
      );
    }
  }
}
